import { Suspense, useEffect, useState } from 'react';
import { FileExcelOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Input,
  Segmented,
  Select,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EmptyIcon } from '@/assets/icons';
import DataTable from '@/components/data-table';
import TableHeaderSearch from '@/components/data-table/TableHeaderSearch';
import TableHeaderSort from '@/components/data-table/TableHeaderSort';
import TableHeaderTitle from '@/components/data-table/TableHeaderTitle';
import useRowOpen from '@/components/data-table/useRowOpen';
import useEmployeeNoticesT, {
  errorText,
} from '@/components/email-templates/useEmployeeNoticesT';
import { DataTableSkeleton } from '@/components/skeletons';
import { ROUTES } from '@/constants';
import LocalQueryParamsProvider from '@/contexts/query-params/LocalQueryParamsProvider';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useDebounce } from '@/hooks';
import { exportCompanyEquipmentOverview } from '@/services/equipmentRequests.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import useCompanyEquipmentOverview from './useCompanyEquipmentOverview';

const SHOW_OPTIONS = ['assigned', 'requested', 'returned', 'all'];

const STATE_COLORS = {
  requested: 'gold',
  assigned: 'blue',
  returned: 'green',
};

// The panel's query params, kept in the URL under the `eq_` prefix so that
// "back" from an employee returns to the same page, filters and sort. Every
// key must be listed: a prefixed provider reads only these.
const PANEL_DEFAULT_PARAMS = Object.freeze({
  offset: '1',
  limit: '10',
  show: 'assigned',
  service: undefined,
  search: undefined,
  employee: undefined,
  sector: undefined,
  item: undefined,
  sortEmployeeName: undefined,
  sortSectorName: undefined,
  sortName: undefined,
  sortService: undefined,
  sortState: undefined,
  sortSince: undefined,
});

// Column searches (header magnifier) -> their query param.
const COLUMN_SEARCHES = ['employee', 'sector', 'item'];

const asText = (value) =>
  Array.isArray(value) ? value.join(',') : value || '';
const day = (date) =>
  date ? dayjs(date).format(DATE_FORMATS.DISPLAY_DOTS) : '—';

/**
 * "Equipment" tile of the company profile: every item asked for the
 * company's employees — who holds what now, what still waits to be handed
 * over, what came back — with counters, filters and an Excel export, in the
 * standard list table (server-side paging and sorting). A row opens the
 * employee's profile on its "Equipment" tile.
 */
export default function CompanyEquipmentPanel({ companyId }) {
  return (
    <LocalQueryParamsProvider defaultParams={PANEL_DEFAULT_PARAMS} prefix="eq_">
      <CompanyEquipmentTable companyId={companyId} />
    </LocalQueryParamsProvider>
  );
}

function CompanyEquipmentTable({ companyId }) {
  const { tn } = useEmployeeNoticesT();
  const to = (key, opts) => tn(`equipmentOverview.${key}`, opts);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [exporting, setExporting] = useState(false);

  const { data, status, error } = useCompanyEquipmentOverview(
    companyId,
    queryParams
  );
  const counts = data?.counts || {};
  const total = data?.count || 0;

  const openRow = useRowOpen(
    (row) =>
      `${ROUTES.admin}/${ROUTES.employees}/${row.employeeId}?company=${companyId}&tile=oprema`
  );

  // The free search writes to the URL a moment after typing stops.
  const [searchInput, setSearchInput] = useState(asText(queryParams.search));
  useEffect(() => {
    const value = asText(queryParams.search);
    setSearchInput((prev) => (prev.trim() === value ? prev : value));
  }, [queryParams.search]);
  const writeSearch = useDebounce((value) => {
    setQueryParams({ search: value.trim() || undefined, offset: 1 });
  }, 400);

  // Column searches: the typed text per column, and which box is open.
  const [searchTerm, setSearchTerm] = useState({});
  const [openSearch, setOpenSearch] = useState({});
  useEffect(() => {
    setSearchTerm(
      Object.fromEntries(
        COLUMN_SEARCHES.map((key) => [key, asText(queryParams[key])])
      )
    );
  }, [queryParams]);
  const onSearchOpen = (name, value) =>
    setOpenSearch((prev) => ({ ...prev, [name]: value }));
  const onSearchChange = (name, value) =>
    setSearchTerm((prev) => ({ ...prev, [name]: value }));
  const onSearchSubmit = (name, form) => {
    onSearchOpen(name, false);
    const value = asText(form[name]).trim();
    setQueryParams({ [name]: value || undefined, offset: 1 });
  };
  const onSearchReset = (name) => {
    onSearchOpen(name, false);
    setSearchTerm((prev) => ({ ...prev, [name]: '' }));
    setQueryParams({ [name]: undefined, offset: 1 });
  };
  const columnSearch = (name, title, placement) => (
    <TableHeaderSearch
      dataIndex={name}
      title={title}
      searchTerm={searchTerm}
      onChange={onSearchChange}
      onSubmit={onSearchSubmit}
      onReset={onSearchReset}
      toggleDropdown={openSearch}
      onClose={onSearchOpen}
      placement={placement}
    />
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCompanyEquipmentOverview({
        ...queryParams,
        company: companyId,
      });
    } catch (err) {
      message.error(errorText(err, to('exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: () => (
        <TableHeaderTitle title={to('colEmployee')}>
          {columnSearch('employee', to('colEmployee'), 'bottomLeft')}
          <TableHeaderSort dataIndex="employeeName" />
        </TableHeaderTitle>
      ),
      dataIndex: 'employeeName',
      width: 210,
      render: (text, row) => (
        <div>
          <div className="font-semibold">{text}</div>
          {row.leaving && (
            <Tooltip title={to('leavingHint')}>
              <Tag color="red" className="!mt-0.5 !text-[11px]">
                {row.endDate
                  ? to('leavingOn', { date: day(row.endDate) })
                  : to('leaving')}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <TableHeaderTitle title={to('colSector')}>
          {columnSearch('sector', to('colSector'))}
          <TableHeaderSort dataIndex="sectorName" />
        </TableHeaderTitle>
      ),
      dataIndex: 'sectorName',
      width: 170,
      render: (text) => text || '—',
    },
    {
      title: () => (
        <TableHeaderTitle title={to('colItem')}>
          {columnSearch('item', to('colItem'))}
          <TableHeaderSort dataIndex="name" />
        </TableHeaderTitle>
      ),
      dataIndex: 'name',
      render: (_, row) => (
        <div>
          {row.group && row.group !== row.name && (
            <div className="text-[11px] uppercase tracking-wide text-zinc-400">{row.group}</div>
          )}
          <span className="font-medium">{row.name}</span>
          {row.extra && (
            <span className="ml-1 text-[11.5px] text-zinc-400">
              ({to('extra')})
            </span>
          )}
          {row.detail && (
            <div className="text-xs text-zinc-500">{row.detail}</div>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <TableHeaderTitle title={to('colService')}>
          <TableHeaderSort dataIndex="service" />
        </TableHeaderTitle>
      ),
      dataIndex: 'service',
      width: 130,
      render: (text) => text || '—',
    },
    {
      title: () => (
        <TableHeaderTitle title={to('colState')}>
          <TableHeaderSort dataIndex="state" />
        </TableHeaderTitle>
      ),
      dataIndex: 'state',
      width: 140,
      render: (state) => (
        <Tag color={STATE_COLORS[state]}>{to(`states.${state}`)}</Tag>
      ),
    },
    {
      title: () => (
        <TableHeaderTitle
          title={<Tooltip title={to('sinceHint')}>{to('colSince')}</Tooltip>}
        >
          <TableHeaderSort dataIndex="since" />
        </TableHeaderTitle>
      ),
      dataIndex: 'since',
      width: 110,
      render: day,
    },
    {
      title: to('colNote'),
      dataIndex: 'note',
      width: 220,
      ellipsis: { showTitle: true },
      render: (text) => text || '—',
    },
  ];

  if (error) {
    return (
      <p className="m-0 text-[13px] text-red-600">
        {errorText(error, to('loadFailed'))}
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
        <span>
          {to('countAssigned')}: <b>{counts.assigned ?? '—'}</b>
        </span>
        <span className="text-zinc-300">·</span>
        <span>
          {to('countRequested')}: <b>{counts.requested ?? '—'}</b>
        </span>
        <span className="text-zinc-300">·</span>
        <span className={counts.leaving ? 'text-red-600' : ''}>
          {to('countLeaving')}: <b>{counts.leaving ?? '—'}</b>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-500">{to('show')}</span>
        <Segmented
          size="small"
          value={queryParams.show || 'assigned'}
          onChange={(show) => setQueryParams({ show, offset: 1 })}
          options={SHOW_OPTIONS.map((value) => ({
            value,
            label: to(`showOptions.${value}`),
          }))}
        />
        <Select
          size="small"
          className="!w-44"
          value={asText(queryParams.service)}
          onChange={(service) =>
            setQueryParams({ service: service || undefined, offset: 1 })
          }
          options={[
            { value: '', label: to('allServices') },
            ...(data?.services || []).map((name) => ({
              value: name,
              label: name,
            })),
          ]}
        />
        <Input.Search
          allowClear
          size="small"
          className="!w-64"
          placeholder={to('search')}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            writeSearch(e.target.value);
          }}
          onSearch={(value) =>
            setQueryParams({ search: value.trim() || undefined, offset: 1 })
          }
        />
      </div>

      <p className="m-0 text-[12.5px] text-zinc-500">{to('panelHint')}</p>

      {/* DataTable is lazy: its own fallback, not the page's. */}
      <Suspense fallback={<DataTableSkeleton columnLength={columns.length} />}>
        <DataTable
          syncUrl={false}
          rowKey="key"
          title={`${to('tableTitle')} (${total})`}
          data={data?.items || []}
          total={total}
          columns={columns}
          status={status}
          offset={queryParams.offset}
          limit={queryParams.limit}
          onChangePagination={(key, value) => setQueryParams({ [key]: value })}
          onRow={openRow}
          metadata={{ columnLength: columns.length }}
          locale={{
            emptyText: (
              <Empty
                className="grid gap-2 py-8"
                description={to('empty')}
                image={<EmptyIcon />}
              />
            ),
          }}
          headerChildren={
            <Button
              className="btn-primary-reverse h-8"
              icon={<FileExcelOutlined />}
              loading={exporting}
              disabled={!total}
              onClick={handleExport}
            >
              {to('export')}
            </Button>
          }
        />
      </Suspense>
    </div>
  );
}
